import React, { useState, useCallback, useEffect, createContext } from 'react';
import { NavigationContainer, DefaultTheme as NavigationDefaultTheme, DarkTheme as NavigationDarkTheme, createNavigationContainerRef } from '@react-navigation/native';
import { Provider as PaperProvider, DefaultTheme as PaperDefaultTheme, MD3DarkTheme as PaperDarkTheme, ActivityIndicator, Button } from 'react-native-paper';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { View, StyleSheet, Platform, useWindowDimensions, TouchableOpacity, DeviceEventEmitter, Text } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import MainNavigator from './src/navigation/MainNavigator';
import AuthNavigator from './src/navigation/AuthNavigator';
import { ScheduleProvider } from './src/contexts/ScheduleContext';
import { auth } from './src/firebase';
import { trackEvent } from './src/firebase'; // Analytics関数をインポート

import { ThemePreferencesContext } from './src/contexts/ThemeContext';
import { ReviewGateProvider } from './src/contexts/ReviewGateContext';
import { GradeGateProvider } from './src/contexts/GradeGateContext';
import GlobalReviewGate from './src/components/GlobalReviewGate';
import GlobalGradeGate from './src/components/GlobalGradeGate';
import { initializeLanguage } from './src/i18n';

export const navigationRef = createNavigationContainerRef();

// カスタムテーマを定義 (オプション)
const CustomDefaultTheme = {
  ...PaperDefaultTheme,
  ...NavigationDefaultTheme,
  colors: {
    ...PaperDefaultTheme.colors,
    ...NavigationDefaultTheme.colors,
    recovery: '#C95A33',
    onRecovery: '#FFFFFF',
    recoveryContainer: '#FCECE7',
    onRecoveryContainer: '#5C2E22',
  },
};

const CustomDarkTheme = {
  ...PaperDarkTheme,
  ...NavigationDarkTheme,
  colors: {
    ...PaperDarkTheme.colors,
    ...NavigationDarkTheme.colors,
    recovery: '#E07A55',
    onRecovery: '#FFFFFF',
    recoveryContainer: '#3E2A26',
    onRecoveryContainer: '#FCECE7',
  },
};

const INITIAL_METRICS = {
  frame: { x: 0, y: 0, width: 375, height: 812 }, // デフォルトのモバイルサイズ
  insets: { top: 0, left: 0, right: 0, bottom: 0 },
};

// 簡易的なエラーバウンダリ
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: any }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error("App Error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <View style={{ flex: 1, backgroundColor: '#2196F3', justifyContent: 'center', alignItems: 'center', padding: 24 }}>
          <View style={{ marginBottom: 40, alignItems: 'center' }}>
            <Ionicons name="cloud-offline-outline" size={120} color="white" />
          </View>

          <Text style={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: 28 }}>
            Something went wrong.
          </Text>

          <Text style={{ marginTop: 16, color: 'rgba(255,255,255,0.9)', textAlign: 'center', fontSize: 16, lineHeight: 24 }}>
            {(this.state.error as any)?.toString() || "Network response timed out"}
          </Text>

          <View style={{ flexDirection: 'row', marginTop: 60, gap: 16 }}>
            <Button
              mode="contained"
              buttonColor="white"
              textColor="#2196F3"
              contentStyle={{ height: 50, paddingHorizontal: 10 }}
              labelStyle={{ fontSize: 16, fontWeight: 'bold' }}
              icon={({ size, color }) => <Ionicons name="home" size={size} color={color} />}
              onPress={() => {
                this.setState({ hasError: false, error: null });
                // Note: In a real app, we might want to force navigation to the home screen here
              }}
            >
              Home
            </Button>

            <Button
              mode="contained"
              buttonColor="white"
              textColor="#2196F3"
              contentStyle={{ height: 50, paddingHorizontal: 10 }}
              labelStyle={{ fontSize: 16, fontWeight: 'bold' }}
              icon={({ size, color }) => <Ionicons name="refresh" size={size} color={color} />}
              onPress={() => this.setState({ hasError: false, error: null })}
            >
              Refresh
            </Button>
          </View>

          {/* Diagnostic info for developers */}
          {__DEV__ && (
            <Text style={{ marginTop: 40, color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
              Debug: {this.state.error?.message}
            </Text>
          )}
        </View>
      );
    }
    return <View style={{ flex: 1 }}>{this.props.children}</View>;
  }
}

export default function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [isGuest, setIsGuest] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false);

  useEffect(() => {
    const initGuest = async () => {
      try {
        const [guestStr] = await Promise.all([
          AsyncStorage.getItem('@user_is_guest'), initializeLanguage(),
        ]);
        if (guestStr === 'true') setIsGuest(true);
      } catch (e) {
        console.error('Error reading guest status', e);
      } finally {
        setPreferencesReady(true);
      }
    };
    initGuest();

    const listener = DeviceEventEmitter.addListener('auth_guest_change', (value) => {
      setIsGuest(value);
      if (value) {
        AsyncStorage.setItem('@user_is_guest', 'true').catch(console.error);
      } else {
        AsyncStorage.removeItem('@user_is_guest').catch(console.error);
      }
    });

    return () => {
      listener.remove();
    };
  }, []);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      setInitializing(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (initializing || !preferencesReady) return;
    if (user) trackEvent('user_signed_in', { user_id: user.uid });
    else if (isGuest) trackEvent('guest_user_session', { session_type: 'guest' });
  }, [user, isGuest, initializing, preferencesReady]);

  // アプリ起動時のAnalyticsイベント
  useEffect(() => {
    trackEvent('app_open', {
      platform: Platform.OS,
      app_version: '2.1.0',
      timestamp: new Date().toISOString()
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDarkMode(prevMode => !prevMode);
  }, []);

  const theme = isDarkMode ? CustomDarkTheme : CustomDefaultTheme;

  if (initializing || !preferencesReady) {
    return (
      <PaperProvider theme={theme}>
        <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      </PaperProvider>
    );
  }

  return (
    <View style={StyleSheet.absoluteFill}>
      <ThemePreferencesContext.Provider value={{ toggleTheme, isDarkMode }}>
        <SafeAreaProvider style={StyleSheet.absoluteFill}>
          <PaperProvider theme={theme}>
            <ErrorBoundary>
              <ScheduleProvider>
                <View style={{ flex: 1 }}>
                  <NavigationContainer ref={navigationRef} theme={theme}>
                    {user || isGuest ? (
                      <ReviewGateProvider>
                        <GradeGateProvider>
                          <>
                            <MainNavigator />
                            <GlobalReviewGate />
                            <GlobalGradeGate />
                          </>
                        </GradeGateProvider>
                      </ReviewGateProvider>
                    ) : (
                      <AuthNavigator />
                    )}
                  </NavigationContainer>
                </View>
              </ScheduleProvider>
            </ErrorBoundary>
          </PaperProvider>
        </SafeAreaProvider>
      </ThemePreferencesContext.Provider>
    </View>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
